import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
  Inject,
  PLATFORM_ID,
  Renderer2,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';

export interface TocSection {
  id: string;
  label: string;
  active: boolean;
}
@Component({
    selector: 'app-terms',
    imports: [CommonModule, RouterModule],
    templateUrl: './terms.component.html',
    styleUrl: './terms.component.scss'
})
export class TermsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('docContent', { static: false }) docContent!: ElementRef;

  /** Detect if we're running in the beta environment */
  isBeta = false;

  /** Last updated date shown in hero */
  lastUpdated = '5th March 2026';

  /** Table of contents sections */
  tocSections: TocSection[] = [
    { id: 's1', label: '1. Introduction & Agreement', active: false },
    { id: 's2', label: '2. Definitions', active: false },
    { id: 's3', label: '3. Eligibility & Age', active: false },
    { id: 's4', label: '4. License to Use', active: false },
    { id: 's5', label: '5. User Accounts', active: false },
    { id: 's6', label: '6. Subscriptions & Payments', active: false },
    { id: 's7', label: '7. Acceptable Use', active: false },
    { id: 's8', label: '8. AI Features & Code', active: false },
    { id: 's9', label: '9. Intellectual Property', active: false },
    { id: 's10', label: '10. Data & Privacy', active: false },
    { id: 's11', label: '11. Cyber Security & Protection', active: false },
    { id: 's12', label: '12. Beta Version', active: false },
    { id: 's13', label: '13. Disclaimer of Warranties', active: false },
    { id: 's14', label: '14. Limitation of Liability', active: false },
    { id: 's15', label: '15. Indemnification', active: false },
    { id: 's16', label: '16. Term & Termination', active: false },
    { id: 's17', label: '17. Governing Law & Disputes', active: false },
    { id: 's18', label: '18. Export Controls', active: false },
    { id: 's19', label: '19. Copyright Policy', active: false },
    { id: 's20', label: '20. Modifications', active: false },
    { id: 's21', label: '21. Miscellaneous', active: false },
    { id: 's22', label: '22. Contact', active: false },
  ];

  private intersectionObserver?: IntersectionObserver;
  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private meta: Meta,
    private titleService: Title,
    private renderer: Renderer2,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.detectEnvironment();
    this.setMetaTags();
    this.applySecurityHeaders();
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.initScrollSpy();
    }
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  // ──────────────────────────────────────────────
  // Environment Detection
  // ──────────────────────────────────────────────

  private detectEnvironment(): void {
    if (this.isBrowser) {
      const hostname = window.location.hostname;
      this.isBeta =
        hostname.startsWith('beta.') ||
        hostname.includes('beta.angularize') ||
        hostname.includes('localhost'); // treat local as beta during dev; remove for prod
    }
  }

  get platformLabel(): string {
    return this.isBeta ? 'beta.angularize.dev' : 'angularize.dev';
  }

  get platformUrl(): string {
    return this.isBeta
      ? 'https://beta.angularize.dev'
      : 'https://angularize.dev';
  }

  // ──────────────────────────────────────────────
  // SEO & Meta Tags
  // ──────────────────────────────────────────────

  private setMetaTags(): void {
    const title = this.isBeta
      ? 'Terms of Service (Beta) — Angularize.dev'
      : 'Terms of Service — Angularize.dev';

    this.titleService.setTitle(title);

    const description =
      'Read the Terms of Service for Angularize.dev, owned by Pythrust Technologies OPC Private Limited. Governs use of our Angular development platform and beta version.';

    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({
      property: 'og:url',
      content: `${this.platformUrl}/terms-of-service`,
    });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary' });
    this.meta.updateTag({ name: 'twitter:title', content: title });

    // Canonical — always points to production URL
    if (this.isBrowser) {
      this.ensureCanonical('https://angularize.dev/terms-of-service');
    }
  }

  private ensureCanonical(url: string): void {
    let link: HTMLLinkElement = document.querySelector(
      "link[rel='canonical']",
    ) as HTMLLinkElement;
    if (!link) {
      link = this.renderer.createElement('link') as HTMLLinkElement;
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.appendChild(document.head, link);
    }
    this.renderer.setAttribute(link, 'href', url);
  }

  // ──────────────────────────────────────────────
  // Security Headers (client-side hardening)
  // Note: true CSP/security headers must be set server-side.
  // These meta tags are a best-effort client-side supplement.
  // ──────────────────────────────────────────────

  private applySecurityHeaders(): void {
    if (!this.isBrowser) return;

    // Referrer Policy
    this.meta.updateTag({
      name: 'referrer',
      content: 'strict-origin-when-cross-origin',
    });

    // Prevent clickjacking via meta (supplement to X-Frame-Options header)
    this.meta.updateTag({
      'http-equiv': 'X-UA-Compatible',
      content: 'IE=edge',
    });

    // Content-Security-Policy meta tag (supplement; always prefer server-side headers)
    this.meta.updateTag({
      'http-equiv': 'Content-Security-Policy',
      content: [
        "default-src 'self'",
        "script-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
        "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://fonts.gstatic.com",
        "font-src 'self' https://fonts.gstatic.com",
        "img-src 'self' data: https:",
        "connect-src 'self'",
        "frame-ancestors 'none'",
        "base-uri 'self'",
        "form-action 'self'",
      ].join('; '),
    });

    // Disable MIME-type sniffing
    this.meta.updateTag({
      'http-equiv': 'X-Content-Type-Options',
      content: 'nosniff',
    });

    // Prevent IE compatibility mode
    this.meta.updateTag({ 'http-equiv': 'X-Frame-Options', content: 'DENY' });
  }

  // ──────────────────────────────────────────────
  // Scroll Spy
  // ──────────────────────────────────────────────

  private initScrollSpy(): void {
    const options: IntersectionObserverInit = {
      rootMargin: '-15% 0px -80% 0px',
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.tocSections.forEach((s) => {
            s.active = s.id === entry.target.id;
          });
        }
      });
    }, options);

    this.tocSections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) this.intersectionObserver!.observe(el);
    });
  }

  // ──────────────────────────────────────────────
  // TOC Click — smooth scroll
  // ──────────────────────────────────────────────

  scrollTo(id: string): void {
    if (!this.isBrowser) return;
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // ──────────────────────────────────────────────
  // Keyboard accessibility: ESC to return to top
  // ──────────────────────────────────────────────

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.isBrowser) window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ──────────────────────────────────────────────
  // Prevent right-click on legal text (optional soft protection)
  // ──────────────────────────────────────────────

  @HostListener('contextmenu', ['$event'])
  onContextMenu(event: MouseEvent): void {
    // Allow default — we don't block copy but record intent
    // Remove this if you want to allow copy freely
  }

  // ──────────────────────────────────────────────
  // Definition table data
  // ──────────────────────────────────────────────

  definitions = [
    {
      term: 'Platform / Services',
      desc: 'The Angularize.dev web application, beta.angularize.dev, APIs, documentation, and all related tools.',
    },
    {
      term: 'User / You',
      desc: 'Any individual or entity that accesses, registers on, or uses the Platform in any capacity.',
    },
    {
      term: 'Account',
      desc: 'The registered profile created by a User to access Platform features.',
    },
    {
      term: 'Content',
      desc: 'Any code, text, data, files, or materials created, uploaded, or processed through the Platform.',
    },
    {
      term: 'User Content',
      desc: 'Content originating from or owned by the User, submitted to or processed on the Platform.',
    },
    {
      term: 'Generated Output',
      desc: "Code, scaffolding, components, and artifacts produced by the Platform's AI systems in response to User inputs.",
    },
    {
      term: 'Input',
      desc: 'Prompts, commands, requirements, or instructions submitted by a User to the Platform.',
    },
    {
      term: 'Subscription Plan',
      desc: 'A paid or free tier that determines the features and usage limits available to a User.',
    },
    {
      term: 'Beta Version',
      desc: 'The pre-release Platform accessible at beta.angularize.dev, provided for testing and evaluation.',
    },
    {
      term: 'Company',
      desc: 'Pythrust Technologies OPC Private Limited, registered and operating in India.',
    },
    {
      term: 'Cyber Attack',
      desc: "Any unauthorised attempt to access, disrupt, degrade, destroy, or misuse the Platform's systems, data, or infrastructure.",
    },
  ];
}
